import { ID, TaskFilterOptions, TaskType } from '@griply/types';

import {
  FirestoreInstance,
  OrderByDirection,
  WhereFilterOp,
} from '../types/firestore';
import { FirestoreCollections } from '../types/firestore-collections';

export type GetTasksQueryOptions<
  TCollection = unknown,
  TQuery = unknown,
  TConstraint = unknown,
  TDb = FirestoreInstance,
> = {
  db: TDb;
  collection: (
    db: NoInfer<TDb>,
    path: string,
    ...rest: string[]
  ) => TCollection;
  query: (ref: TCollection, ...constraints: TConstraint[]) => TQuery;
  where: (field: string, op: WhereFilterOp, value: unknown) => TConstraint;
  orderBy: (field: string, direction?: OrderByDirection) => TConstraint;

  uid: ID;
} & TaskFilterOptions;

export const getTasksQuery = <
  TCollection,
  TQuery,
  TConstraint,
  TDb = FirestoreInstance,
>(
  options: GetTasksQueryOptions<TCollection, TQuery, TConstraint, TDb>,
): TQuery => {
  const {
    db,
    collection,
    query,
    where,
    orderBy,

    uid,
    ...filters
  } = options;

  const constraints = [
    where('roles.all', 'array-contains', uid),

    !filters.showCompleted && where('completedAt', '==', null),

    !!filters.tasksOnly && where('type', '==', TaskType.Task),
    !!filters.ids?.length && where('id', 'in', filters.ids),
    filters.lifeAreas?.length && where('lifeAreaId', 'in', filters.lifeAreas),
    !!filters.noLifeArea &&
      !filters.lifeAreas?.length &&
      where('lifeAreaId', '==', null),
    filters.goals?.length && where('goalId', 'in', filters.goals),
    !!filters.noGoal && !filters.goals?.length && where('goalId', '==', null),
    filters.deadlineDeadlineStartDate !== undefined &&
      where('deadlineDeadline', '>=', filters.deadlineDeadlineStartDate),
    filters.deadlineDeadlineEndDate !== undefined &&
      where('deadlineDeadline', '<=', filters.deadlineDeadlineEndDate),
    !!filters.noDeadlineDeadline && where('deadlineDeadline', '==', null),
    !!filters.hasDeadlineDeadline && where('deadlineDeadline', '!=', null),
    // Don't narrow by endStrategy.deadline when fetching completed tasks —
    // the date filter represents the completion-date window, not the task
    // deadline, so applying it here would exclude tasks without a deadline.
    !filters.showCompleted &&
      filters.deadlineStartDate !== undefined &&
      where('endStrategy.deadline', '>=', filters.deadlineStartDate),
    !filters.showCompleted &&
      filters.deadlineEndDate !== undefined &&
      where('endStrategy.deadline', '<=', filters.deadlineEndDate),
    !!filters.noDeadline && where('endStrategy.deadline', '==', null),
    !!filters.hasDeadline && where('endStrategy.deadline', '!=', null),
    // Only narrow Firestore reads by completedAt when the consumer is
    // explicitly fetching completed tasks. Otherwise, range constraints on
    // completedAt would exclude null-valued (uncompleted) tasks that other
    // pages still expect to receive.
    !!filters.showCompleted &&
      filters.completedStartDate !== undefined &&
      where('completedAt', '>=', filters.completedStartDate),
    !!filters.showCompleted &&
      filters.completedEndDate !== undefined &&
      where('completedAt', '<=', filters.completedEndDate),
  ].filter(Boolean) as TConstraint[];

  return query(
    collection(db, FirestoreCollections.Tasks),
    ...constraints,
    orderBy('createdAt', 'asc'),
  );
};
